import React, { createContext, PropsWithChildren, useContext, useEffect, useMemo, useState } from 'react'
import client from 'api/auth'

interface Feedback2API {
  hasFeedback: () => boolean
  getReview: () => SubjectRating | null
  getQuestionsRatings: () => SubjectRating[]
  getItemRatings: () => SubjectRating[]
  getSessionAverage: () => number
}

const Feedback2Ctx = createContext<Feedback2API | null>(null)

export interface Session {
  average: number
}
export interface Subject {
  id: number
  type_: 'item' | 'review' | 'question'
  name: string
  description: string
  min: number
  max: number
  channel_id: number
  restaurant_id: number
}
export interface Rating {
  id: number
  score: number
  comment: null | string
  min: number
  max: number
  created: string
  update: string
  subject_id: number
}

export interface FeedbackData {
  session: Session
  ratings: Rating[]
  subjects: Subject[]
}

export interface SubjectRating {
  subject: Subject
  rating: null | Rating
}

function loadFeedback(orderId: string): Promise<FeedbackData> {
  const token = client.getAuthResponse().access_token
  return fetch(`${FEEDBACK_SVC2}/sessions/${orderId}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  }).then((res: Response) => (res.ok ? res.json() : Promise.reject({ statusCode: res.status })))
}

export function Feedback2Svc(props: PropsWithChildren<{ orderId: string }>): JSX.Element {
  const [feedbackData, setFeedbackData] = useState<FeedbackData | null>(null)
  useEffect(() => {
    loadFeedback(props.orderId).then(setFeedbackData)
  }, [props.orderId])

  const subjectRatingLookup = useMemo<{ [key: number]: SubjectRating }>(() => {
    return feedbackData?.subjects.reduce(
      (sum, s) => ({
        ...sum,
        [s.id]: { subject: s, rating: feedbackData.ratings.find((r) => r.subject_id == s.id) || null },
      }),
      {}
    )
  }, [feedbackData])

  const api = {
    getSessionAverage: () => feedbackData.session.average,
    getReview: (): SubjectRating | null => {
      const s = feedbackData.subjects.find((r) => r.type_ == 'review')
      return s ? subjectRatingLookup[s.id] : null
    },
    getQuestionsRatings: (): SubjectRating[] =>
      feedbackData.subjects.filter((s) => s.type_ == 'question').map((s) => subjectRatingLookup[s.id]),
    getItemRatings: (): SubjectRating[] =>
      feedbackData.subjects.filter((s) => s.type_ == 'item').map((s) => subjectRatingLookup[s.id]),
    hasFeedback: () => feedbackData != null,
  }
  return (
    <Feedback2Ctx.Provider key={props.orderId} value={api}>
      {props.children}
    </Feedback2Ctx.Provider>
  )
}

export function useFeedback2API(): Feedback2API {
  const api = useContext(Feedback2Ctx)
  return api as Feedback2API
}
